<template>
	<iframe
		v-if="hubs.currentHubExists"
		:id="iframeHubId"
		allow="camera; microphone; display-capture; autoplay; speaker-selection; clipboard-write; fullscreen"
		:src="hubUrl"
		class="h-full w-full"
		name="hub"
	></iframe>
</template>

<script lang="ts" setup>
	// Packages
	import { onMounted, onUnmounted, ref, watch } from 'vue';
	import { useRoute, useRouter } from 'vue-router';

	import { cacheBust } from '@global-client/logic/utils/cacheBust';

	import { createLogger } from '@hub-client/logic/logging/Logger';
	import { assert } from '@hub-client/logic/utils/assert';
	// Logic
	import { delay } from '@hub-client/logic/utils/common';

	import { type EnterStartResp } from '@global-client/models/MSS/TGeneral';

	// Stores
	import { useGlobal } from '@global-client/stores/global';
	import { useHubs } from '@global-client/stores/hubs';
	import { useMSS } from '@global-client/stores/mss';

	import { iframeHubId } from '@hub-client/stores/messagebox';

	const route = useRoute();
	const router = useRouter();
	const hubs = useHubs();
	const global = useGlobal();
	const logger = createLogger('Hub');

	onMounted(onRouteChange);

	onUnmounted(() => {
		hubs.changeHub({
			name: '',
			roomId: '',
		});
	});

	watch(route, onRouteChange);

	const hubUrl = ref('');

	async function onRouteChange() {
		let hubId = undefined;
		const maxAttempts = 4;
		for (let attempt = 0; attempt < maxAttempts && !hubId; attempt++) {
			try {
				hubId = hubs.hubId(route.params.name as string);
			} catch (error) {
				logger.error(`Could not execute function onRouteChange on attempt: ${attempt}`, { error });
			}
			if (!hubId) {
				await delay(attempt);
			}
		}
		if (!hubId) {
			router.push({ name: 'home' });
			return;
		}
		if (!hubs.hubExists(hubId)) {
			await hubs.changeHub({ id: '', roomId: '' });
		}
		await handleHubAuth(hubId);
		await hubs.changeHub(route.params);
	}

	async function handleHubAuth(id: string) {
		const hub = hubs.hub(id);
		if (!hub) return;
		const hubId = hub.hubId ?? '';
		const state = hubloggedinstatus(hubId);

		switch (state.kind) {
			case Status.GlobalNotLoggedIn:
				hubUrl.value = `${hub.url}?${new URLSearchParams({ hubId, cb: cacheBust })}#/hub/`;
				break;
			case Status.MSSHubNotLoggedIn: {
				try {
					const maxAttempts = 4;
					for (let attempt = 0; attempt < maxAttempts; attempt++) {
						if (attempt > 0) {
							await delay(attempt - 1);
						}
						const mss = useMSS();
						const enterStartResp: EnterStartResp | undefined = await hub.enterStartEP();
						assert.isDefined(enterStartResp, 'Something went wrong receiving/handling the response from enterStartEP.');
						const hhpp = await mss.enterHub(id, enterStartResp);
						assert.isDefined(hhpp, 'Something went wrong with getting the signedHhpp.');
						const enterCompleteResp = await hub.enterCompleteEP(enterStartResp.state, hhpp);
						if (enterCompleteResp === 'RetryFromStart' && attempt < maxAttempts) continue;
						else if (enterCompleteResp === 'RetryFromStart') throw new Error('Max attemps for RetryFromStart were passed');
						assert.isDefined(enterCompleteResp, 'Something went wrong receiving/handling a response from enterCompleteEP.');
						const authInfo = JSON.stringify({
							token: enterCompleteResp.access_token,
							userId: enterCompleteResp.mxid,
						});
						// TODO make sure that accessToken is no longer passed in query parameter
						hubUrl.value = `${hub.url}?${new URLSearchParams({ newToken: 'true', accessToken: authInfo, hubId, cb: cacheBust })}`;
						break;
					}
				} catch (error) {
					logger.error('Error during Hub MSS login', { error });
					router.push({ name: 'error' });
				}
				break;
			}
			case Status.MSSAccessToken: {
				const authInfo = JSON.stringify({
					token: state.token,
					userId: state.userId,
				});
				// TODO: make sure that accessToken is no longer passed in query parameter
				hubUrl.value = `${hub.url}?${new URLSearchParams({ accessToken: authInfo, hubId, cb: cacheBust })}`;
				break;
			}
		}
	}

	enum Status {
		GlobalNotLoggedIn,
		MSSAccessToken,
		MSSHubNotLoggedIn,
	}

	type HubLoggedInStatus = GlobalNotLoggedIn | MSSAccessToken | MSSHubNotLoggedIn;

	interface GlobalNotLoggedIn {
		kind: Status.GlobalNotLoggedIn;
	}

	interface MSSHubNotLoggedIn {
		kind: Status.MSSHubNotLoggedIn;
	}

	interface MSSAccessToken {
		kind: Status.MSSAccessToken;
		token: string;
		userId: string;
	}

	function hubloggedinstatus(hubid: string): HubLoggedInStatus {
		if (!global.loggedIn) {
			return { kind: Status.GlobalNotLoggedIn };
		}

		const authInfo = global.getAuthInfo(hubid);
		if (authInfo) {
			return {
				kind: Status.MSSAccessToken,
				token: authInfo.token,
				userId: authInfo.userId,
			};
		}

		return { kind: Status.MSSHubNotLoggedIn };
	}
</script>
